import React from 'react';
import { MdEdit } from 'react-icons/md';
import { formatInt, formatQty, toNumber } from '../../../utils/billToOrderCalc';
import { itemPlanningValue } from '../../../utils/billToItemCatalog';

const cellInputClasses =
  'w-full rounded-lg border border-gray-200 bg-[#F7E5D5] px-2 py-1.5 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';

const cellReadOnlyClasses =
  'w-full rounded-lg border border-gray-200 bg-[#F7E5D5]/50 px-2 py-1.5 text-sm cursor-default focus:border-gray-200 focus:outline-none focus:ring-0';

const COLUMNS = [
  { label: 'SKU' },
  { label: 'SKU Name' },
  { label: 'SPS 1' },
  { label: 'SPS 2' },
  { label: 'SPS 3' },
  { label: 'Lead Time (Days)' },
  { label: 'Buffer %' },
  { label: 'Growth %' },
  { label: 'Projected Sales' },
  { label: 'Projected GC' },
  { label: 'Build To' },
  { label: 'Closing Stock' },
  { label: 'Suggested Order' },
  { label: 'Final Order' },
  { label: 'Final Order by Outlet', editable: true },
  { label: 'SPS Ord 1' },
  { label: 'SPS Ord 2' },
  { label: 'SPS Ord 3' },
];

function sanitizeNumeric(raw, allowNegative) {
  const value = String(raw ?? '');
  const pattern = allowNegative ? /^-?\d*\.?\d*$/ : /^\d*\.?\d*$/;
  if (value === '' || pattern.test(value)) return value;
  return null;
}

function displayPlanningValue(value) {
  const text = String(value ?? '').trim();
  return text === '' ? '—' : text;
}

function displayProjectedMetric(value, formatter) {
  if (value == null || value === '') return '—';
  const n = toNumber(value);
  if (!Number.isFinite(n)) return '—';
  return formatter(n);
}

function ReadOnlyCell({ value }) {
  return (
    <td className="border-b border-gray-100 px-2.5 py-1.5">
      <input
        type="text"
        readOnly
        tabIndex={-1}
        value={value}
        className={cellReadOnlyClasses}
        aria-readonly="true"
      />
    </td>
  );
}

function SkuPlanningTable({
  rows,
  wizardData,
  allowEditOutlet = false,
  onOutletOrderChange,
  maxHeightClass = 'max-h-[420px]',
}) {
  return (
    <div className={`${maxHeightClass} overflow-auto`}>
      <table className="min-w-[1880px] border-collapse text-sm">
        <thead>
          <tr>
            {COLUMNS.map(({ label, editable }) => (
              <th
                key={label}
                className="sticky top-0 z-10 border-b border-amber-200 bg-[#F7E5D5] px-2.5 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-700"
              >
                <span className="inline-flex items-center gap-1">
                  {label}
                  {editable ? (
                    <MdEdit
                      className="h-3.5 w-3.5 shrink-0 text-red-700"
                      title="Editable"
                      aria-hidden
                    />
                  ) : null}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(
            ({
              item,
              projectedSales,
              projectedGc,
              buildTo,
              suggestedOrder,
              finalOrder,
              finalOrderByOutlet,
              closingStock,
              sps1,
              sps2,
              sps3,
              spsOrd1,
              spsOrd2,
              spsOrd3,
            }) => (
              <tr key={item.id} className="hover:bg-amber-50/40">
                <td className="border-b border-gray-100 px-2.5 py-1.5 text-gray-700">
                  {item.itemCode}
                </td>
                <td className="border-b border-gray-100 px-2.5 py-1.5 font-medium text-gray-900">
                  <span className="block">{item.itemName}</span>
                  <span className="block text-xs font-normal text-gray-500">
                    {item.subCategory || item.category}
                  </span>
                </td>
                <ReadOnlyCell value={displayPlanningValue(sps1)} />
                <ReadOnlyCell value={displayPlanningValue(sps2)} />
                <ReadOnlyCell value={displayPlanningValue(sps3)} />
                <ReadOnlyCell
                  value={displayPlanningValue(itemPlanningValue(item, wizardData, 'leadTime'))}
                />
                <ReadOnlyCell
                  value={displayPlanningValue(
                    itemPlanningValue(item, wizardData, 'bufferPercent')
                  )}
                />
                <ReadOnlyCell
                  value={displayPlanningValue(
                    itemPlanningValue(item, wizardData, 'projectedGrowth')
                  )}
                />
                <ReadOnlyCell value={displayProjectedMetric(projectedSales, formatInt)} />
                <ReadOnlyCell value={displayProjectedMetric(projectedGc, formatInt)} />
                <ReadOnlyCell value={displayProjectedMetric(buildTo, (n) => formatQty(n, 1))} />
                <ReadOnlyCell value={displayPlanningValue(closingStock)} />
                <ReadOnlyCell
                  value={displayProjectedMetric(suggestedOrder, (n) => formatQty(n, 1))}
                />
                <ReadOnlyCell value={displayProjectedMetric(finalOrder, formatInt)} />
                <td className="border-b border-gray-100 px-2.5 py-1.5">
                  {allowEditOutlet ? (
                    <input
                      type="text"
                      inputMode="decimal"
                      value={finalOrderByOutlet}
                      placeholder={finalOrder == null ? '—' : String(finalOrder)}
                      onChange={(e) => {
                        const next = sanitizeNumeric(e.target.value, false);
                        if (next != null) onOutletOrderChange?.(item, next);
                      }}
                      className={cellInputClasses}
                      aria-label="Final Order by Outlet"
                    />
                  ) : (
                    <input
                      type="text"
                      readOnly
                      tabIndex={-1}
                      value={displayPlanningValue(finalOrderByOutlet)}
                      className={cellInputClasses}
                      aria-readonly="true"
                    />
                  )}
                </td>
                <ReadOnlyCell value={displayProjectedMetric(spsOrd1, formatInt)} />
                <ReadOnlyCell value={displayProjectedMetric(spsOrd2, formatInt)} />
                <ReadOnlyCell value={displayProjectedMetric(spsOrd3, formatInt)} />
              </tr>
            )
          )}
        </tbody>
      </table>
    </div>
  );
}

export default SkuPlanningTable;
